/**
 * Safe Listening Service V1
 * Confort d'écoute - Listening comfort management
 * 
 * SOFT CAP: 
 * - Gentle reminder (once per session)
 * - Visual indicator badge
 * 
 * HARD CAP:
 * - Automatic intensity adjustment
 * - Cannot exceed this level
 */

// Volume thresholds as percentages (0-100 scale from UI)
// BUILD 52: Raised SOFT_CAP to 85% to allow 80% default without warning
const SOFT_CAP_PERCENT = 85;  // Warning threshold (was 80%)
const HARD_CAP_PERCENT = 90;  // Absolute maximum (was 85%)

class SafeListeningService {
  constructor() {
    this.warningShownThisSession = false;
    this.enabled = true; // Can be disabled by user in settings (premium feature?)
    this.onWarningCallback = null;
    this.onCapReachedCallback = null;
    
    // Session tracking
    this.sessionStartTime = null;
    this.totalHighVolumeTime = 0; // Time spent above soft cap (ms)
    this.highVolumeStartTime = null;
  }

  /**
   * Reset session state (call on new session start)
   */
  resetSession() {
    this.warningShownThisSession = false;
    this.sessionStartTime = Date.now();
    this.totalHighVolumeTime = 0;
    this.highVolumeStartTime = null;
  }

  /**
   * Set callback for when warning should be shown
   * @param {Function} callback - Called with { message, level }
   */
  setWarningCallback(callback) {
    this.onWarningCallback = callback;
  }

  /**
   * Set callback for when hard cap is reached
   * @param {Function} callback - Called with { message, cappedValue }
   */
  setCapReachedCallback(callback) {
    this.onCapReachedCallback = callback;
  }

  /**
   * Enable or disable safe listening limits
   * @param {boolean} enabled 
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    console.log(`[SafeListening] ${enabled ? 'Enabled' : 'Disabled'}`);
  }

  /**
   * Apply safe listening limits to a volume value
   * @param {number} requestedVolume - Requested volume (0-100 scale)
   * @param {string} lang - User language for messages ('fr' or 'en')
   * @returns {{ 
   *   volume: number, 
   *   capped: boolean, 
   *   warning: boolean, 
   *   message: string|null,
   *   level: 'safe'|'warning'|'danger'
   * }}
   */
  applyLimits(requestedVolume, lang = 'fr') {
    if (!this.enabled) {
      return { 
        volume: requestedVolume, 
        capped: false, 
        warning: false, 
        message: null,
        level: 'safe'
      };
    }

    let finalVolume = requestedVolume;
    let capped = false;
    let warning = false;
    let message = null;
    let level = 'safe';

    // Track high volume time
    if (requestedVolume >= SOFT_CAP_PERCENT) {
      if (!this.highVolumeStartTime) {
        this.highVolumeStartTime = Date.now();
      }
    } else {
      if (this.highVolumeStartTime) {
        this.totalHighVolumeTime += Date.now() - this.highVolumeStartTime;
        this.highVolumeStartTime = null;
      }
    }

    // Hard cap - intensity maximum
    if (requestedVolume > HARD_CAP_PERCENT) {
      finalVolume = HARD_CAP_PERCENT;
      capped = true;
      level = 'danger';
      
      if (this.onCapReachedCallback) {
        this.onCapReachedCallback({
          message: lang === 'fr' 
            ? 'Volume ajusté pour votre confort d\'écoute'
            : 'Volume adjusted for your listening comfort',
          cappedValue: finalVolume
        });
      }
    }
    // Soft cap - gentle reminder
    else if (requestedVolume >= SOFT_CAP_PERCENT) {
      level = 'warning';
      
      // Show warning only once per session
      if (!this.warningShownThisSession) {
        warning = true;
        this.warningShownThisSession = true;
        message = lang === 'fr'
          ? 'Volume élevé. Privilégiez une écoute apaisante.'
          : 'High volume. Prefer soothing listening.';
        
        if (this.onWarningCallback) {
          this.onWarningCallback({ message, level });
        }
      }
    }

    return { 
      volume: finalVolume, 
      capped, 
      warning, 
      message,
      level
    };
  }

  /**
   * Get current safe listening status
   * @param {number} currentVolume - Current volume (0-100)
   * @returns {{ level: 'safe'|'warning'|'danger', percent: number }}
   */
  getStatus(currentVolume) {
    if (currentVolume >= HARD_CAP_PERCENT) {
      return { level: 'danger', percent: 100 };
    } else if (currentVolume >= SOFT_CAP_PERCENT) {
      // Calculate percentage between soft and hard cap
      const range = HARD_CAP_PERCENT - SOFT_CAP_PERCENT;
      const above = currentVolume - SOFT_CAP_PERCENT;
      const percent = Math.min(100, (above / range) * 100);
      return { level: 'warning', percent };
    }
    return { level: 'safe', percent: 0 };
  }

  /**
   * Get soft cap percentage (for UI)
   */
  getSoftCapPercent() {
    return SOFT_CAP_PERCENT;
  }

  /**
   * Get hard cap percentage (for UI)
   */
  getHardCapPercent() {
    return HARD_CAP_PERCENT;
  }

  /**
   * Check if volume is at or above soft cap
   * @param {number} volume 
   */
  isAboveSoftCap(volume) {
    return volume >= SOFT_CAP_PERCENT;
  }

  /**
   * Check if volume is at hard cap
   * @param {number} volume 
   */
  isAtHardCap(volume) {
    return volume >= HARD_CAP_PERCENT;
  }

  /**
   * Get session statistics
   */
  getSessionStats() {
    let totalTime = this.totalHighVolumeTime;
    if (this.highVolumeStartTime) {
      totalTime += Date.now() - this.highVolumeStartTime;
    }
    
    return {
      sessionDuration: this.sessionStartTime ? Date.now() - this.sessionStartTime : 0,
      highVolumeTime: totalTime,
      warningShown: this.warningShownThisSession
    };
  }
}

// Export singleton instance
const safeListeningService = new SafeListeningService();
export default safeListeningService;
export { SOFT_CAP_PERCENT, HARD_CAP_PERCENT };
